import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Microscope, CheckCircle2, ChevronRight, Scan, Beaker, Activity, BarChart3, Stethoscope, Zap } from 'lucide-react';
import ProgrammePageShell from './ProgrammePageShell';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const BenefitCard = ({ icon: Icon, title, desc }) => (
  <motion.div
    variants={fadeUp}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg p-6 transition-all duration-300 hover:-translate-y-1"
  >
    <div className="w-11 h-11 bg-rose-50 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-[#800000]" />
    </div>
    <h3 className="font-bold text-slate-900 mb-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
  </motion.div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors"
        aria-expanded={open}
      >
        <span>{q}</span>
        <ChevronRight className={`w-5 h-5 text-[#800000] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-3">{a}</div>}
    </div>
  );
};

const AdvancedDiagnostics = () => {
  const benefits = [
    { icon: Scan, title: 'Body Composition Analysis', desc: 'DEXA scan or bioimpedance analysis precisely measures fat mass, lean mass, visceral fat, and bone density — critical data for tailoring interventions.' },
    { icon: Beaker, title: 'Comprehensive Metabolic Panel', desc: 'Full blood workup including fasting insulin, HbA1c, lipid profile, thyroid function, liver enzymes, inflammatory markers (CRP, IL-6), and vitamin levels.' },
    { icon: Activity, title: 'Resting Metabolic Rate Testing', desc: 'Indirect calorimetry determines your true daily caloric needs, preventing the metabolic adaptation trap that causes weight regain after dieting.' },
    { icon: BarChart3, title: 'Hormonal Profiling', desc: 'Leptin, ghrelin, cortisol, sex hormones, and growth hormone levels are assessed to identify hormonal contributors to weight gain or difficulty losing.' },
    { icon: Stethoscope, title: 'Cardiovascular Risk Stratification', desc: 'ECG, echocardiography, and blood pressure monitoring identify cardiovascular risks that must be managed alongside weight loss treatment.' },
    { icon: Zap, title: 'Sleep & Gut Health Assessment', desc: 'Sleep study referrals for suspected obstructive sleep apnoea and gut microbiome analysis help explain metabolic inefficiencies not visible in standard bloodwork.' },
  ];

  const technologies = [
    { name: 'DEXA Body Composition Scanning', detail: 'Gold-standard dual-energy X-ray absorptiometry for precise fat, muscle, and bone measurements.' },
    { name: 'Indirect Calorimetry', detail: 'Breath analysis technology that accurately measures resting metabolic rate and respiratory quotient.' },
    { name: 'Continuous Glucose Monitoring', detail: 'Two-week CGM reveals glycaemic responses to foods, stress, sleep, and activity that standard fasting glucose misses.' },
    { name: 'Ultrasound Elastography', detail: 'Non-invasive liver stiffness measurement to diagnose and grade fatty liver disease (NAFLD/NASH) without biopsy.' },
    { name: 'Advanced Lipid Fractionation', detail: 'Particle size and number analysis of LDL, HDL, and triglycerides for cardiovascular risk beyond standard cholesterol numbers.' },
    { name: 'Hormonal Assay Panel', detail: 'Highly sensitive assays for insulin, cortisol, thyroid hormones, oestrogen, testosterone, and appetite-regulating peptides.' },
  ];

  const faqs = [
    { q: 'Do I need to fast before diagnostic tests?', a: 'Most blood panels require a 10–12 hour overnight fast. We will provide specific pre-test instructions when your appointment is confirmed. Some tests like resting metabolic rate testing require 5 hours of fasting and no exercise for 24 hours beforehand.' },
    { q: 'How long does the diagnostic workup take?', a: 'The full advanced diagnostic workup takes one full day at our centre. Results are compiled and presented to you in a detailed consultation within 2–3 working days.' },
    { q: 'Is the DEXA scan safe?', a: 'Yes. A DEXA scan for body composition uses very low levels of X-ray radiation — far less than a standard chest X-ray. It takes approximately 10 minutes and is painless. It is not recommended during pregnancy.' },
    { q: 'Can I get these tests done at my local lab?', a: 'Standard panels can be done anywhere. However, specialised tests like indirect calorimetry, DEXA body composition, and continuous glucose monitoring require specialist equipment available at our centre.' },
    { q: 'Will my diagnostic results change over time?', a: 'Yes, and that is the point. We repeat selected tests at key milestones (3, 6, 12 months) to track clinical improvements in metabolic health, not just weight — because normalising your metabolic markers is the true measure of success.' },
  ];

  return (
    <>
      <Helmet>
        <title>Advanced Diagnostics for Obesity | GastroCare Bhopal</title>
        <meta name="description" content="GastroCare Bhopal uses state-of-the-art diagnostics — DEXA scanning, metabolic rate testing, hormonal profiling, and CGM — to identify the precise causes of weight gain and build the most effective treatment plan." />
        <meta property="og:title" content="Advanced Diagnostics | GastroCare Bhopal" />
        <meta property="og:description" content="Comprehensive obesity diagnostics including body composition analysis, metabolic rate testing, hormonal profiling, and cardiovascular risk stratification." />
      </Helmet>

      <ProgrammePageShell
        title="Advanced Diagnostics"
        subtitle="Precision medicine starts with precision measurement. Our advanced diagnostic programme goes beyond standard blood tests to uncover the hidden metabolic, hormonal, and structural drivers of your weight condition."
        icon={Microscope}
        breadcrumb="Advanced Diagnostics"
      >
        {/* Introduction */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp}>
              <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Why Diagnostics Matter</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                You Cannot Fix What You Cannot Measure
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                The most common reason weight loss efforts fail is treating obesity as a simple calorie equation while ignoring the complex biological machinery underneath. Conditions like insulin resistance, thyroid dysfunction, cortisol dysregulation, and visceral fat accumulation respond to different interventions — and without measuring them, treatment is guesswork.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                At GastroCare Bhopal, our advanced diagnostics programme uses technology typically found in metabolic research centres to generate a clinically precise profile of your body's condition. This data drives every decision in your treatment — from the calories prescribed to the specific medications or procedures considered.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Knowledge is the foundation of lasting change. When you understand exactly why your body stores fat and resists weight loss, the path forward becomes clear and science-backed.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <img
                src={process.env.PUBLIC_URL + "/img-2.png"}
                alt="Advanced diagnostics laboratory at GastroCare"
                className="rounded-2xl w-full h-72 lg:h-96 object-cover shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#800000] text-white rounded-2xl px-5 py-4 shadow-lg hidden md:block">
                <p className="text-2xl font-extrabold leading-none">15+</p>
                <p className="text-sm opacity-90">Diagnostic Parameters</p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Diagnostics Available */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Our Capabilities</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>What We Measure</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {benefits.map((b, i) => <BenefitCard key={i} {...b} />)}
          </div>
        </motion.section>

        {/* Technologies */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16 bg-slate-900 rounded-3xl p-8 lg:p-12">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">State-of-the-Art</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Advanced Technologies We Use</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
            {technologies.map((tech, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <h3 className="font-bold text-white mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{tech.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{tech.detail}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Who Benefits */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="max-w-3xl mx-auto mb-10">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Eligibility</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Who Benefits Most from Advanced Diagnostics?</h2>
            <div className="space-y-4 pt-2">
              {[
                'Patients with unexplained weight gain despite a healthy lifestyle',
                'Individuals with a family history of metabolic disorders',
                'Patients with Type 2 diabetes, PCOS, or thyroid conditions',
                'Anyone preparing for bariatric surgery requiring pre-operative assessment',
                'Patients who have regained weight after previous weight loss',
                'Athletes or active individuals seeking optimised body composition',
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#800000] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.section>

        {/* FAQs */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Frequently Asked Questions</h2>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => <FAQ key={i} q={f.q} a={f.a} />)}
          </div>
        </motion.section>
      </ProgrammePageShell>
    </>
  );
};

export default AdvancedDiagnostics;
